import { Button } from "@chakra-ui/react/button";
import { Flex } from "@chakra-ui/react/flex";
import { Heading } from "@chakra-ui/react/heading";
import { useBreakpointValue } from "@chakra-ui/react/hooks";
import { Spacer } from "@chakra-ui/react/spacer";
import { Text } from "@chakra-ui/react/text";

import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from "components/ui/dialog";
import { Switch } from "components/ui/switch";
import {
  useMarkAllNotificationsRead,
  useNotifications,
} from "hooks/queries/use-notifications";
import React, { useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import NotificationItem from "./NotificationItem";

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationsModal = ({ isOpen, onClose }: NotificationsModalProps) => {
  const { colorScheme } = useAppSelector((state) => state.theme);
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  const { data: notifications, isLoading: isLoadingNotifications } =
    useNotifications();
  const markAllNotificationsAsReadMutation = useMarkAllNotificationsRead();

  const [isUnreadOnly, setIsUnreadOnly] = useState<boolean>(false);

  const filterNotifications = useMemo(() => {
    if (!notifications) return [];
    if (isUnreadOnly) {
      return notifications.filter((notification) => !notification.read);
    }
    return notifications;
  }, [notifications, isUnreadOnly]);

  const handleMarkAllAsRead = () => {
    markAllNotificationsAsReadMutation.mutate();
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open: newIsOpen }) => {
        if (!newIsOpen) {
          onClose();
        }
      }}
      size={isBaseSize ? "full" : "md"}
    >
      <DialogContent>
        <DialogHeader>
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            <Heading textStyle="heading.4">Notifications</Heading>
            <Spacer />
            <Text mr={1}>Unreads</Text>
            <Switch
              size="sm"
              checked={isUnreadOnly}
              colorPalette={colorScheme}
              onCheckedChange={(e) => setIsUnreadOnly(e.checked)}
            />
          </Flex>
        </DialogHeader>
        <DialogBody maxH="60vh" overflowY="auto">
          {filterNotifications.length === 0 ? (
            <Text>No new notifications</Text>
          ) : (
            filterNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
              />
            ))
          )}
        </DialogBody>
        <DialogFooter display="flex" w="100%" justifyContent="space-between">
          <Button colorPalette="gray" variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            mr={1}
            colorPalette={colorScheme}
            onClick={handleMarkAllAsRead}
          >
            Mark all as read
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default NotificationsModal;
